/**
 * LoginForm — OTP-based phone authentication
 * Optimized: uses authAPI service, useAsync hook, clean state management
 */

import React, { useState, useCallback } from "react";
import { Form, Input, Button, Space, App, Row, Col } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { authAPI } from "../../api/services";
import { useAuth } from "../../context/AuthContext";
import PoweredBy from "../ui/PoweredBy";

const LoginForm = ({ origin }) => {
  const { notification } = App.useApp();
  const { login } = useAuth();
  const [form] = Form.useForm();

  const [phone, setPhone] = useState("");
  const [otpRef, setOtpRef] = useState("");
  const [loading, setLoading] = useState(false);

  // ─── Step 1: Send OTP ──────────────────────────────────────────────────────
  const handleSendOTP = useCallback(
    async ({ phone: phoneValue }) => {
      setLoading(true);
      try {
        const res = await authAPI.sendOTP(phoneValue, origin);
        setPhone(phoneValue);
        setOtpRef(res?.data?.otp_ref || "");
      } catch (err) {
        notification.error({ message: err.message });
      } finally {
        setLoading(false);
      }
    },
    [origin, notification]
  );

  // ─── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOTP = useCallback(
    async (otp) => {
      if (otp.length < 4) return;
      setLoading(true);
      try {
        const res = await authAPI.verifyOTP(phone, otp, otpRef, origin);
        const { credentials, user_data, org_data } = res?.data || {};
        if (credentials?.access_token && user_data && org_data) {
          login(credentials, user_data, org_data);
        } else {
          notification.error({ message: "Invalid OTP. Please try again." });
        }
      } catch (err) {
        notification.error({ message: err.message });
      } finally {
        setLoading(false);
      }
    },
    [phone, otpRef, origin, login, notification]
  );

  // ─── OTP Screen ───────────────────────────────────────────────────────────
  if (otpRef) {
    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row align="middle" gutter={10}>
          <ArrowLeftOutlined
            onClick={() => setOtpRef("")}
            style={{ fontSize: 20, cursor: "pointer" }}
          />
          <Col flex={1} style={{ textAlign: "center" }}>
            Enter OTP sent to{" "}
            <span style={{ fontWeight: 600 }}>{`+91 ${phone}`}</span>
          </Col>
        </Row>

        <Input.OTP
          autoFocus
          size="large"
          length={4}
          variant="underlined"
          disabled={loading}
          onChange={handleVerifyOTP}
        />

        <Footer />
      </Space>
    );
  }

  // ─── Phone Screen ─────────────────────────────────────────────────────────
  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Form
        form={form}
        size="large"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSendOTP}
      >
        <Form.Item
          name="phone"
          rules={[
            { required: true, message: "Please enter your phone number" },
            {
              pattern: /^[6-9]\d{9}$/,
              message: "Enter a valid 10-digit mobile number",
            },
          ]}
        >
          <Input
            prefix="+91"
            autoFocus
            maxLength={10}
            placeholder="Enter mobile number"
            type="tel"
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          block
          loading={loading}
          style={{ height: 48, borderRadius: 8, fontWeight: 600 }}
        >
          Send OTP
        </Button>
      </Form>

      <Footer />
    </Space>
  );
};

// ─── Shared Footer ─────────────────────────────────────────────────────────────
const Footer = () => (
  <>
    <Col align="middle" style={{ marginTop: 8 }}>
      <div className="description" style={{ fontSize: 12 }}>
        By proceeding, I accept the T&C and Privacy Policy
      </div>
    </Col>
    <Col align="middle" style={{ paddingTop: 8 }}>
      <PoweredBy />
    </Col>
  </>
);

export default LoginForm;